import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { IoClose } from "react-icons/io5";
import { FiUpload } from "react-icons/fi";
import { AiOutlineFilePdf } from "react-icons/ai";
import { FaTimes, FaFile, FaTag, FaCheck, FaHome } from "react-icons/fa";
import { RxCross2 } from "react-icons/rx";
import { useMediaQuery, useTheme } from "@mui/material";
import { toast } from "react-toastify";
import {
  getUpdatesFiles,
  uploadUpdatesFile,
  deleteUpdatesFile,
} from "../../../../../services/scopeUpdateService";
import { getApiData } from "../../../../../utils/APIHelper";

const DEFAULT_IMAGE =
  "https://renohome.blob.core.windows.net/reno-cms/e56d3d53-e335-425f-990e-16e6b2bbee1b";

const GALLERY_TAB_PDF = "__pdf__";
const GALLERY_TAB_OTHER = "__other__";

export const tagsToMetaData = (tags) => {
  if (!tags?.length) return { room: null, milestones: [], tags: [] };
  const nameTag = tags.find((t) => t.type === "name");
  const milestoneTags = tags.filter((t) => t.type === "milestone");
  return {
    room: nameTag?.label ?? null,
    milestones: milestoneTags.map((t) => ({
      id: t.refId,
      milestone_name: t.label,
    })),
    tags: tags.map((t) => ({
      type: t.type,
      label: t.label,
      refId: t.refId ?? null,
      x: t.x,
      y: t.y,
    })),
  };
};

const metaDataToTags = (metaData) => {
  if (!metaData) return [];
  if (metaData.tags && Array.isArray(metaData.tags) && metaData.tags.length > 0) {
    return metaData.tags.map((t, i) => ({
      id: t.id || `tag_${i}_${Date.now()}`,
      type: t.type,
      label: t.label,
      refId: t.refId ?? null,
      x: t.x ?? 10,
      y: t.y ?? 10 + i * 10,
    }));
  }
  const tags = [];
  let y = 10;
  if (metaData.room) {
    tags.push({
      id: `tag_room_${Date.now()}`,
      type: "name",
      label: metaData.room,
      x: 10,
      y,
      refId: null,
    });
    y += 15;
  }
  (metaData.milestones || []).forEach((m, i) => {
    tags.push({
      id: `tag_m_${m.id}_${i}`,
      type: "milestone",
      label: m.milestone_name || m.name,
      refId: m.id,
      x: 10,
      y: y + i * 10,
    });
  });
  return tags;
};

const parseMeta = (raw) => {
  if (!raw) return {};
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
};

const getRoomName = (meta) => meta?.room_name ?? meta?.room ?? null;

const isImageExt = (ext) =>
  typeof ext === "string" && ext.startsWith("image/");
const isPdfExt = (ext) => ext === "application/pdf";

/* ---- inline style helpers ---- */

const subTabBase = {
  padding: "8px 16px",
  borderRadius: 10,
  border: "1px solid #e5e7eb",
  background: "#fff",
  color: "#4b5563",
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",
  fontFamily: "Inter Tight",
};
const subTabActive = {
  ...subTabBase,
  background: "#4D6BF6",
  color: "#fff",
  border: "1px solid #4D6BF6",
};

const pillBase = (active) => ({
  padding: "6px 12px",
  borderRadius: 9999,
  border: "none",
  fontSize: 12,
  fontWeight: 500,
  fontFamily: "Inter Tight",
  cursor: "pointer",
  background: active ? "#4D6BF6" : "#f3f4f6",
  color: active ? "#fff" : "#4b5563",
  textTransform: "capitalize",
});

const tagPillStyle = (type) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "4px 10px",
  borderRadius: 9999,
  fontSize: 11,
  fontWeight: 500,
  color: "#fff",
  background: type === "name" ? "#2563eb" : "#16a34a",
  whiteSpace: "nowrap",
});

const formatImageDate = (item) =>
  item.created_at ? new Date(item.created_at) : new Date();

const fmtDate = (d) =>
  d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

export default function UploadSection({
  projectId,
  updateId,
  uploadedFiles,
  setUploadedFiles,
  activities = [],
  disabled = false,
  onActivitiesForPayload,
  otherImages = [],
  otherPdfFiles = [],
  isCreateMode = false,
  hasUpdateId = false,
  activeSubTab: activeSubTabProp,
  setActiveSubTab: setActiveSubTabProp,
  hideInternalSubTabs = false,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const galleryAvailable = hasUpdateId;
  const [internalSubTab, setInternalSubTab] = useState(
    galleryAvailable && !isCreateMode ? "gallery" : "annotate"
  );
  const activeSubTab = activeSubTabProp ?? internalSubTab;
  const setActiveSubTab = setActiveSubTabProp ?? setInternalSubTab;

  useEffect(() => {
    if (!galleryAvailable && activeSubTab === "gallery") {
      setActiveSubTab("annotate");
    }
  }, [galleryAvailable, activeSubTab, setActiveSubTab]);

  /* server state */
  const [serverFiles, setServerFiles] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [roomsPage, setRoomsPage] = useState(1);
  const [roomsHasMore, setRoomsHasMore] = useState(false);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [roomSearchQuery, setRoomSearchQuery] = useState("");

  /* annotate state */
  const [annotatingIndex, setAnnotatingIndex] = useState(null);
  const [pendingPin, setPendingPin] = useState(null);
  const [pinStep, setPinStep] = useState(null); // "name" | "milestone" | null
  const [savingImage, setSavingImage] = useState(false);
  const [hoveredTagId, setHoveredTagId] = useState(null);

  /* gallery viewer state */
  const [selectedGalleryRoom, setSelectedGalleryRoom] = useState(null);
  const [viewingImage, setViewingImage] = useState(null);

  const dropdownRef = useRef(null);
  const nameInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const selectNextAfterSaveRef = useRef(null);

  useEffect(() => {
    if (typeof onActivitiesForPayload === "function") {
      onActivitiesForPayload(activities);
    }
  }, [activities, onActivitiesForPayload]);

  const refetchFiles = async () => {
    if (!updateId) return;
    try {
      const res = await getUpdatesFiles(updateId);
      if (res?.success) setServerFiles(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (updateId) refetchFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateId]);

  /* Fetch rooms (search + pagination). Debounce simple: refetch when query or page changes. */
  useEffect(() => {
    if (!projectId) return;
    const q = roomSearchQuery.trim();
    if (q.length > 0 && q.length < 3) return;

    setRoomsLoading(true);
    const params = new URLSearchParams({ page: String(roomsPage), per_page: "50" });
    if (q.length >= 3) params.set("search", q);

    getApiData(`api/project/${projectId}/rooms?${params.toString()}`, "GET", {})
      .then((res) => {
        if (!res?.success) return;
        const items = res?.data || res?.rooms || [];
        setRoomsHasMore(Boolean(res?.next_enable));
        setRooms((prev) => (roomsPage === 1 ? items : [...prev, ...items]));
      })
      .catch((err) => console.error("rooms fetch failed", err))
      .finally(() => setRoomsLoading(false));
  }, [projectId, roomsPage, roomSearchQuery]);

  /* Reset rooms pagination when search query changes */
  useEffect(() => {
    setRoomsPage(1);
  }, [roomSearchQuery]);

  /* Merge server files (with id) into uploadedFiles so both lists share state */
  useEffect(() => {
    if (!serverFiles?.length) {
      setUploadedFiles((prev) => prev.filter((p) => !p.id));
      return;
    }
    setUploadedFiles((prev) => {
      const merged = serverFiles.map((f) => ({
        id: f.id,
        url: f.image_url,
        path: f.image_url,
        extension: f.extension,
        file_extention: f.extension,
        meta_data: parseMeta(f.meta_data),
        created_at: f.created_at,
        uniqueId: `saved_${f.id}`,
      }));
      const pending = prev.filter((p) => !p.id);
      return [...merged, ...pending];
    });
  }, [serverFiles, setUploadedFiles]);

  /* ---- derived lists ---- */

  const pendingFiles = useMemo(
    () => (uploadedFiles || []).filter((f) => !f.id),
    [uploadedFiles]
  );

  const savedImagesWithRoom = useMemo(
    () =>
      (uploadedFiles || []).filter(
        (f) =>
          f.id &&
          getRoomName(f.meta_data) &&
          (isImageExt(f.extension) || isImageExt(f.file_extention))
      ),
    [uploadedFiles]
  );

  const savedPdfs = useMemo(
    () =>
      (uploadedFiles || []).filter(
        (f) => f.id && (isPdfExt(f.extension) || isPdfExt(f.file_extention))
      ),
    [uploadedFiles]
  );

  const roomGroups = useMemo(() => {
    const groups = {};
    savedImagesWithRoom.forEach((img) => {
      const room = getRoomName(img.meta_data) || "Unnamed room";
      if (!groups[room]) groups[room] = [];
      groups[room].push(img);
    });
    return groups;
  }, [savedImagesWithRoom]);

  const normalizedOtherImages = useMemo(
    () =>
      (otherImages || []).map((img) => ({
        ...img,
        id: img.id,
        url: img.path || img.url,
        path: img.path || img.url,
        uniqueId: `other_${img.id}`,
        meta_data: {},
        created_at: img.created_at,
        file_extention: img.file_extention,
      })),
    [otherImages]
  );
  const normalizedOtherPdfs = useMemo(
    () =>
      (otherPdfFiles || []).map((pdf) => ({
        ...pdf,
        id: pdf.id,
        url: pdf.path || pdf.url,
        path: pdf.path || pdf.url,
        uniqueId: `other_pdf_${pdf.id}`,
        meta_data: {},
        created_at: pdf.created_at,
        file_extention: pdf.file_extention || "application/pdf",
      })),
    [otherPdfFiles]
  );
  const hasOtherMedia =
    normalizedOtherImages.length > 0 || normalizedOtherPdfs.length > 0;

  /* ---- annotating file ---- */

  const annotatingFile =
    annotatingIndex !== null ? uploadedFiles?.[annotatingIndex] : null;
  const annotatingId =
    annotatingFile?.uniqueId || `idx_${annotatingIndex}`;
  const currentTags = useMemo(
    () =>
      annotatingFile?.meta_data
        ? metaDataToTags(annotatingFile.meta_data)
        : [],
    [annotatingFile]
  );
  const hasName = currentTags.some((t) => t.type === "name");
  const isAnnotatingPdf =
    annotatingFile &&
    (isPdfExt(annotatingFile.file_extention) ||
      isPdfExt(annotatingFile.extension) ||
      isPdfExt(annotatingFile.type));
  const canSaveAnnotated =
    annotatingFile &&
    !annotatingFile.id &&
    annotatingFile.file &&
    updateId &&
    (hasName || isAnnotatingPdf);

  /* Auto-select next pending file after save, or first pending on mount */
  useEffect(() => {
    const uidToSelect = selectNextAfterSaveRef.current;
    if (uidToSelect != null) {
      const idx = (uploadedFiles || []).findIndex(
        (f) => f.uniqueId === uidToSelect
      );
      if (idx >= 0) setAnnotatingIndex(idx);
      selectNextAfterSaveRef.current = null;
      return;
    }
    if (annotatingIndex !== null && !uploadedFiles?.[annotatingIndex]) {
      const next = (uploadedFiles || []).findIndex((f) => !f.id);
      setAnnotatingIndex(next >= 0 ? next : null);
    } else if (annotatingIndex === null && pendingFiles.length > 0) {
      const firstPendingIdx = (uploadedFiles || []).findIndex((f) => !f.id);
      if (firstPendingIdx >= 0) setAnnotatingIndex(firstPendingIdx);
    }
  }, [uploadedFiles, pendingFiles.length, annotatingIndex]);

  useEffect(() => {
    if (pinStep === "name" && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, [pinStep]);

  /* ---- file add / remove ---- */

  const handleFilePick = (e) => {
    if (disabled) return;
    const files = Array.from(e.target.files || []);
    const allowed = [
      "image/jpeg",
      "image/png",
      "image/gif",
      "image/webp",
      "application/pdf",
    ];
    const MAX = 50 * 1024 * 1024;
    const toAdd = [];
    for (const file of files) {
      if (!allowed.includes(file.type)) {
        toast.error("Only images (JPEG, PNG, GIF, WebP) and PDFs are allowed.");
        continue;
      }
      if (file.size > MAX) {
        toast.error(`File "${file.name}" exceeds 50 MB limit`);
        continue;
      }
      if (uploadedFiles.length + toAdd.length >= 25) {
        toast.error("Maximum 25 files allowed at a time.");
        break;
      }
      toAdd.push({
        file,
        url: URL.createObjectURL(file),
        extension: file.type,
        file_extention: file.type,
        name: file.name,
        meta_data: { room: null, milestones: [], tags: [] },
        uniqueId: `new_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
        created_at: new Date().toISOString(),
      });
    }
    if (toAdd.length) setUploadedFiles((prev) => [...prev, ...toAdd]);
    e.target.value = "";
  };

  const handleRemovePending = (item) => {
    setUploadedFiles((prev) =>
      prev.filter((f) => f.uniqueId !== item.uniqueId)
    );
  };

  const handleDeleteSaved = async (fileId) => {
    try {
      const res = await deleteUpdatesFile(fileId);
      if (res?.success) {
        toast.success("File deleted");
        setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
        refetchFiles();
      } else {
        toast.error(res?.message || "Delete failed");
      }
    } catch (err) {
      toast.error(err?.message || "Delete failed");
    }
  };

  /* ---- pin / tag handlers (annotate canvas) ---- */

  const updateAnnotatingTags = (updater) => {
    if (!annotatingFile) return;
    setUploadedFiles((prev) =>
      prev.map((f) => {
        if (f.uniqueId !== annotatingFile.uniqueId) return f;
        const nextTags = updater(metaDataToTags(f.meta_data));
        return { ...f, meta_data: tagsToMetaData(nextTags) };
      })
    );
  };

  const handleImageClick = (e) => {
    if (disabled) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setPendingPin({ x, y });
    setPinStep(hasName ? "milestone" : "name");
  };

  const addNameTag = useCallback(
    (room) => {
      if (!pendingPin || !room?.room_name) return;
      const tag = {
        id: `tag_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
        type: "name",
        label: room.room_name,
        refId: room.room_id ?? null,
        x: pendingPin.x,
        y: pendingPin.y,
      };
      updateAnnotatingTags((tags) => [...tags, tag]);
      setPendingPin(null);
      setPinStep(null);
      setRoomSearchQuery("");
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pendingPin, annotatingFile]
  );

  const addMilestoneTag = (activityOrMilestone) => {
    if (!pendingPin) return;
    const label =
      activityOrMilestone.activity_name ||
      activityOrMilestone.milestone_name ||
      activityOrMilestone.name;
    const tag = {
      id: `tag_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
      type: "milestone",
      label,
      refId: activityOrMilestone.id,
      x: pendingPin.x,
      y: pendingPin.y,
    };
    updateAnnotatingTags((tags) => [...tags, tag]);
    setPendingPin(null);
    setPinStep(null);
  };

  const removeTag = (tagId) => {
    updateAnnotatingTags((tags) => tags.filter((t) => t.id !== tagId));
  };

  const getPopoverStyle = (pin) => {
    const style = { position: "absolute", zIndex: 50 };
    if (pin.x > 65) style.right = `${100 - pin.x}%`;
    else style.left = `${pin.x}%`;
    if (pin.y > 60) style.bottom = `${100 - pin.y}%`;
    else style.top = `${pin.y}%`;
    return style;
  };

  const handleRoomScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 20) {
      if (roomsHasMore && !roomsLoading) setRoomsPage((p) => p + 1);
    }
  };

  /* ---- save annotated file ---- */

  const handleSaveAnnotated = async () => {
    if (!canSaveAnnotated) return;
    const currentIdxInPending = pendingFiles.findIndex(
      (f) => f.uniqueId === annotatingFile.uniqueId
    );
    const nextFile = pendingFiles[currentIdxInPending + 1];
    setSavingImage(true);
    const meta = isAnnotatingPdf
      ? { room: null, milestones: [], tags: [] }
      : tagsToMetaData(currentTags);
    try {
      const res = await uploadUpdatesFile({
        refId: updateId,
        file: annotatingFile.file,
        metaData: meta,
      });
      if (res?.success) {
        toast.success("File saved");
        selectNextAfterSaveRef.current = nextFile?.uniqueId ?? null;
        setUploadedFiles((prev) =>
          prev.filter((f) => f.uniqueId !== annotatingFile.uniqueId)
        );
        setAnnotatingIndex(null);
        setPendingPin(null);
        setPinStep(null);
        refetchFiles();
      } else {
        toast.error(res?.message || "Upload failed");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to save");
    } finally {
      setSavingImage(false);
    }
  };

  /* ---------------- Render ---------------- */

  return (
    <div style={{ width: "100%", paddingBottom: 8 }}>
      {!hideInternalSubTabs && (
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <button
            type="button"
            style={activeSubTab === "annotate" ? subTabActive : subTabBase}
            onClick={() => setActiveSubTab("annotate")}
          >
            Add Activity Photos
          </button>
          {galleryAvailable && (
            <button
              type="button"
              style={activeSubTab === "gallery" ? subTabActive : subTabBase}
              onClick={() => setActiveSubTab("gallery")}
            >
              Gallery
            </button>
          )}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,application/pdf"
        multiple
        hidden
        onChange={handleFilePick}
        disabled={disabled}
      />

      {/* ---------------- ANNOTATE TAB ---------------- */}
      {activeSubTab === "annotate" && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 3fr",
            gap: 24,
          }}
        >
          {/* LHS: pending files list */}
          <div>
            <div
              style={{
                fontSize: 14,
                fontWeight: 500,
                color: "#0d0d0e",
                marginBottom: 8,
              }}
            >
              {pendingFiles.length} file
              {pendingFiles.length !== 1 ? "s" : ""} to annotate
            </div>

            {isMobile ? (
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  overflowX: "auto",
                  paddingBottom: 8,
                  paddingTop: 8,
                }}
              >
                {pendingFiles.length === 0 && (
                  <div
                    onClick={() => !disabled && fileInputRef.current?.click()}
                    style={{
                      flexShrink: 0,
                      width: "100%",
                      minWidth: 200,
                      border: "2px dashed #d1d5db",
                      borderRadius: 12,
                      padding: 24,
                      textAlign: "center",
                      color: "#6b7280",
                      fontSize: 13,
                      cursor: disabled ? "not-allowed" : "pointer",
                      opacity: disabled ? 0.6 : 1,
                    }}
                  >
                    Click to upload images or PDF
                  </div>
                )}
                {pendingFiles.map((item) => {
                  const idxInFull = uploadedFiles.findIndex(
                    (f) => f.uniqueId === item.uniqueId
                  );
                  const isPdf = isPdfExt(item.file_extention || item.extension);
                  const isSelected = annotatingIndex === idxInFull;
                  return (
                    <div
                      key={item.uniqueId}
                      onClick={() => {
                        selectNextAfterSaveRef.current = null;
                        setAnnotatingIndex(idxInFull);
                        setPendingPin(null);
                        setPinStep(null);
                      }}
                      style={{
                        position: "relative",
                        flexShrink: 0,
                        width: 96,
                        height: 96,
                        borderRadius: 12,
                        overflow: "hidden",
                        border: isSelected
                          ? "2px solid #2563eb"
                          : "2px solid #e5e7eb",
                        background: "#fff",
                        cursor: "pointer",
                      }}
                    >
                      {isPdf ? (
                        <div
                          style={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "#f3f4f6",
                          }}
                        >
                          <AiOutlineFilePdf size={32} color="#dc2626" />
                        </div>
                      ) : (
                        <img
                          src={item.url || item.path}
                          alt=""
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      )}
                      {!disabled && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemovePending(item);
                          }}
                          style={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                            padding: 6,
                            borderRadius: 9999,
                            background: "#ef4444",
                            color: "#fff",
                            border: "none",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <FaTimes size={10} />
                        </button>
                      )}
                    </div>
                  );
                })}
                {pendingFiles.length > 0 && !disabled && (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      flexShrink: 0,
                      width: 96,
                      height: 96,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "2px dashed #d1d5db",
                      borderRadius: 12,
                      cursor: "pointer",
                      color: "#6b7280",
                      fontSize: 24,
                    }}
                  >
                    +
                  </div>
                )}
              </div>
            ) : (
              <div
                style={{
                  maxHeight: "70vh",
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  paddingRight: 4,
                  paddingTop: 8,
                }}
              >
                {pendingFiles.length === 0 && (
                  <div
                    onClick={() => !disabled && fileInputRef.current?.click()}
                    style={{
                      border: "2px dashed #d1d5db",
                      borderRadius: 12,
                      padding: 24,
                      textAlign: "center",
                      color: "#6b7280",
                      fontSize: 13,
                      cursor: disabled ? "not-allowed" : "pointer",
                      opacity: disabled ? 0.6 : 1,
                    }}
                  >
                    <FiUpload
                      size={20}
                      style={{ marginBottom: 6, display: "inline-block" }}
                    />
                    <div>Click to upload images or PDF</div>
                  </div>
                )}
                {pendingFiles.map((item) => {
                  const idxInFull = uploadedFiles.findIndex(
                    (f) => f.uniqueId === item.uniqueId
                  );
                  const isPdf = isPdfExt(item.file_extention || item.extension);
                  const isImage = isImageExt(
                    item.file_extention || item.extension
                  );
                  const fileTags = metaDataToTags(item.meta_data);
                  const hasRoom = fileTags.some((t) => t.type === "name");
                  const isSelected = annotatingIndex === idxInFull;

                  return (
                    <div
                      key={item.uniqueId}
                      onClick={() => {
                        selectNextAfterSaveRef.current = null;
                        setAnnotatingIndex(idxInFull);
                        setPendingPin(null);
                        setPinStep(null);
                      }}
                      style={{
                        position: "relative",
                        display: "flex",
                        gap: 8,
                        padding: 8,
                        borderRadius: 8,
                        border: isSelected
                          ? "1px solid #2563eb"
                          : "1px solid #e5e7eb",
                        background: isSelected ? "#eff6ff" : "#fff",
                        cursor: "pointer",
                      }}
                    >
                      <div
                        style={{
                          width: 56,
                          height: 56,
                          borderRadius: 6,
                          overflow: "hidden",
                          background: "#f3f4f6",
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {isPdf ? (
                          <AiOutlineFilePdf size={22} color="#dc2626" />
                        ) : isImage ? (
                          <img
                            src={item.url || item.path}
                            alt=""
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <FaFile size={18} color="#9ca3af" />
                        )}
                      </div>
                      <div
                        style={{
                          flex: 1,
                          minWidth: 0,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                        }}
                      >
                        <p
                          style={{
                            fontSize: 12,
                            color: "#6b7280",
                            margin: 0,
                          }}
                        >
                          {fmtDate(formatImageDate(item))}
                        </p>
                        {!hasRoom && isImage ? (
                          <span
                            style={{
                              fontSize: 10,
                              color: "#d97706",
                              fontWeight: 500,
                              marginTop: 2,
                            }}
                          >
                            Needs room tag
                          </span>
                        ) : (
                          <span
                            style={{
                              fontSize: 10,
                              color: "#6b7280",
                              marginTop: 2,
                            }}
                          >
                            {fileTags.length} tag
                            {fileTags.length !== 1 ? "s" : ""}
                          </span>
                        )}
                      </div>
                      {!disabled && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemovePending(item);
                          }}
                          style={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                            padding: 4,
                            borderRadius: 9999,
                            background: "#ef4444",
                            color: "#fff",
                            border: "none",
                            cursor: "pointer",
                            display: "flex"
                          }}
                        >
                          <FaTimes size={10} />
                        </button>
                      )}
                    </div>
                  );
                })}
                {pendingFiles.length > 0 && !disabled && (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      height: 48,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "2px dashed #d1d5db",
                      borderRadius: 8,
                      cursor: "pointer",
                      color: "#6b7280",
                      fontSize: 20,
                    }}
                  >
                    +
                  </div>
                )}
              </div>
            )}
          </div>

          {/* RHS: canvas */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              minHeight: "60vh",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                marginBottom: 8,
                flexWrap: "wrap",
              }}
            >
              <span style={{ fontSize: 13, color: "#6b7280" }}>
                {annotatingFile
                  ? isAnnotatingPdf
                    ? "PDF file – save to upload"
                    : hasName
                    ? "Tag a milestone"
                    : "Tag the room"
                  : ""}
              </span>
            </div>

            {annotatingFile ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  overflow: "hidden",
                  background: "#fff",
                }}
              >
                {/* Header: step badge */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "8px 16px",
                    borderBottom: "1px solid #e5e7eb",
                    background: "#f9fafb",
                  }}
                >
                  {!isAnnotatingPdf && (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "4px 10px",
                        borderRadius: 9999,
                        fontSize: 11,
                        fontWeight: 500,
                        background: hasName ? "#dcfce7" : "#2563eb",
                        color: hasName ? "#166534" : "#fff",
                      }}
                    >
                      <FaHome size={10} />
                      {hasName ? "Step 2: Tag a milestone" : "Step 1: Tag a Room"}
                    </span>
                  )}
                </div>

                {isAnnotatingPdf ? (
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: 32,
                    }}
                  >
                    <a
                      href={annotatingFile.url || annotatingFile.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 12,
                        padding: 24,
                        borderRadius: 12,
                        border: "2px dashed #e5e7eb",
                        textDecoration: "none",
                      }}
                    >
                      <AiOutlineFilePdf size={48} color="#dc2626" />
                      <span
                        style={{
                          fontSize: 14,
                          fontWeight: 500,
                          color: "#374151",
                        }}
                      >
                        PDF – click to open
                      </span>
                    </a>
                  </div>
                ) : (
                  <>
                    {/* Image canvas */}
                    <div
                      style={{ flex: 1, overflow: "auto", padding: 16 }}
                    >
                      <div
                        style={{
                          position: "relative",
                          display: "inline-block",
                          width: "100%",
                          cursor: "crosshair",
                        }}
                        onClick={handleImageClick}
                      >
                        <img
                          src={annotatingFile.url || annotatingFile.path}
                          alt=""
                          style={{
                            width: "100%",
                            borderRadius: 8,
                            userSelect: "none",
                            objectFit: "contain",
                            background: "#f9fafb",
                            maxHeight: isMobile ? undefined : "55vh",
                            minHeight: isMobile ? "55vh" : undefined,
                          }}
                          draggable={false}
                        />
                        {/* Date overlay */}
                        <div
                          style={{
                            position: "absolute",
                            top: 12,
                            right: 12,
                            background: "rgba(0,0,0,0.7)",
                            color: "#fff",
                            padding: "4px 10px",
                            borderRadius: 6,
                            fontSize: 11,
                            fontWeight: 500,
                            pointerEvents: "none",
                          }}
                        >
                          {fmtDate(formatImageDate(annotatingFile))}
                        </div>
                        {/* Hint */}
                        <div
                          style={{
                            position: "absolute",
                            bottom: 12,
                            left: 12,
                            background: "rgba(0,0,0,0.6)",
                            color: "#fff",
                            padding: "4px 10px",
                            borderRadius: 6,
                            fontSize: 11,
                            pointerEvents: "none",
                          }}
                        >
                          {hasName
                            ? "Click to tag a milestone"
                            : "Click to tag the room"}
                        </div>

                        {/* Existing tag pins */}
                        {currentTags.map((tag) => (
                          <div
                            key={tag.id}
                            style={{
                              position: "absolute",
                              left: `${tag.x}%`,
                              top: `${tag.y}%`,
                              transform: "translate(-50%, -100%)",
                            }}
                            onClick={(e) => e.stopPropagation()}
                            onMouseEnter={() => setHoveredTagId(tag.id)}
                            onMouseLeave={() => setHoveredTagId(null)}
                          >
                            <div style={tagPillStyle(tag.type)}>
                              {tag.type === "name" ? (
                                <FaHome size={9} />
                              ) : (
                                <FaTag size={9} />
                              )}
                              {tag.label}
                              {!disabled && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeTag(tag.id);
                                  }}
                                  style={{
                                    marginLeft: 2,
                                    padding: 2,
                                    borderRadius: 9999,
                                    background: "transparent",
                                    border: "none",
                                    color: "#fff",
                                    cursor: "pointer",
                                    display: "flex"
                                  }}
                                >
                                  <FaTimes size={8} />
                                </button>
                              )}
                            </div>
                            <div
                              style={{
                                width: 2,
                                height: 12,
                                margin: "0 auto",
                                background:
                                  tag.type === "name" ? "#2563eb" : "#16a34a",
                              }}
                            />
                            <div
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: 9999,
                                margin: "0 auto",
                                marginTop: -2,
                                background:
                                  tag.type === "name" ? "#2563eb" : "#16a34a",
                              }}
                            />
                          </div>
                        ))}

                        {/* Pending pin + popover */}
                        {pendingPin && (
                          <>
                            <div
                              style={{
                                position: "absolute",
                                left: `${pendingPin.x}%`,
                                top: `${pendingPin.y}%`,
                                transform: "translate(-50%, -50%)",
                                width: 16,
                                height: 16,
                                borderRadius: 9999,
                                background: "#f97316",
                                border: "2px solid #fff",
                                boxShadow: "0 4px 10px rgba(0,0,0,0.25)",
                                pointerEvents: "none",
                              }}
                            />
                            <div
                              style={getPopoverStyle(pendingPin)}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div
                                style={{
                                  background: "#fff",
                                  borderRadius: 12,
                                  boxShadow:
                                    "0 10px 30px rgba(0,0,0,0.15), 0 2px 6px rgba(0,0,0,0.08)",
                                  border: "1px solid #e5e7eb",
                                  padding: 12,
                                  minWidth: 240,
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    marginBottom: 8,
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: 12,
                                      fontWeight: 500,
                                      color: "#374151",
                                    }}
                                  >
                                    {pinStep === "name"
                                      ? "Add a Name (room)"
                                      : "Select Activity"}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setPendingPin(null);
                                      setPinStep(null);
                                    }}
                                    style={{
                                      background: "transparent",
                                      border: "none",
                                      cursor: "pointer",
                                      color: "#9ca3af",
                                      padding: 2,
                                    }}
                                  >
                                    <RxCross2 size={14} />
                                  </button>
                                </div>

                                {pinStep === "name" ? (
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 8,
                                    }}
                                  >
                                    <div style={{ position: "relative" }}>
                                      <input
                                        ref={nameInputRef}
                                        type="text"
                                        value={roomSearchQuery}
                                        onChange={(e) =>
                                          setRoomSearchQuery(e.target.value)
                                        }
                                        placeholder="Type 3+ chars to search"
                                        style={{
                                          width: "100%",
                                          border: "1px solid #d1d5db",
                                          borderRadius: 8,
                                          padding: "6px 28px 6px 12px",
                                          fontSize: 13,
                                          outline: "none",
                                        }}
                                      />
                                      {roomSearchQuery.length > 0 && (
                                        <button
                                          type="button"
                                          onClick={() => setRoomSearchQuery("")}
                                          style={{
                                            position: "absolute",
                                            right: 8,
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            background: "transparent",
                                            border: "none",
                                            color: "#9ca3af",
                                            cursor: "pointer",
                                            padding: 2,
                                          }}
                                        >
                                          <RxCross2 size={14} />
                                        </button>
                                      )}
                                    </div>
                                    <p
                                      style={{
                                        fontSize: 10,
                                        color: "#6b7280",
                                        margin: 0,
                                      }}
                                    >
                                      Select a room from the list below.
                                    </p>
                                    <div
                                      ref={dropdownRef}
                                      onScroll={handleRoomScroll}
                                      style={{
                                        maxHeight: 250,
                                        overflowY: "auto",
                                        border: "1px solid #e5e7eb",
                                        borderRadius: 8,
                                      }}
                                    >
                                      {roomsLoading && rooms.length === 0 && (
                                        <div
                                          style={{
                                            padding: 12,
                                            textAlign: "center",
                                            fontSize: 11,
                                            color: "#9ca3af",
                                          }}
                                        >
                                          Loading rooms...
                                        </div>
                                      )}
                                      {!roomsLoading &&
                                        roomSearchQuery.trim().length > 0 &&
                                        roomSearchQuery.trim().length < 3 && (
                                          <div
                                            style={{
                                              padding: 12,
                                              textAlign: "center",
                                              fontSize: 11,
                                              color: "#6b7280",
                                            }}
                                          >
                                            Type at least 3 characters to search.
                                          </div>
                                        )}
                                      {!roomsLoading &&
                                        rooms.length === 0 &&
                                        roomSearchQuery.trim().length >= 3 && (
                                          <div
                                            style={{
                                              padding: 12,
                                              textAlign: "center",
                                              fontSize: 11,
                                              color: "#6b7280",
                                            }}
                                          >
                                            No rooms found.
                                          </div>
                                        )}
                                      {rooms.map((room) => (
                                        <button
                                          key={room.room_id || room.id}
                                          type="button"
                                          onClick={() => addNameTag(room)}
                                          style={{
                                            width: "100%",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8,
                                            padding: "8px 12px",
                                            fontSize: 13,
                                            textAlign: "left",
                                            background: "#fff",
                                            border: "none",
                                            borderBottom: "1px solid #f3f4f6",
                                            cursor: "pointer",
                                          }}
                                          onMouseEnter={(e) =>
                                            (e.currentTarget.style.background =
                                              "#eff6ff")
                                          }
                                          onMouseLeave={(e) =>
                                            (e.currentTarget.style.background =
                                              "#fff")
                                          }
                                        >
                                          {room.room_image_url ? (
                                            <img
                                              src={room.room_image_url}
                                              alt=""
                                              style={{
                                                width: 28,
                                                height: 28,
                                                borderRadius: 4,
                                                objectFit: "cover",
                                              }}
                                            />
                                          ) : (
                                            <div
                                              style={{
                                                width: 28,
                                                height: 28,
                                                borderRadius: 4,
                                                background: "#e5e7eb",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                              }}
                                            >
                                              <FaHome size={11} color="#2563eb" />
                                            </div>
                                          )}
                                          <div
                                            style={{
                                              display: "flex",
                                              flexDirection: "column",
                                              minWidth: 0,
                                            }}
                                          >
                                            <span
                                              style={{
                                                fontWeight: 500,
                                                color: "#0d0d0e",
                                              }}
                                            >
                                              {room.room_name}
                                            </span>
                                            <span
                                              style={{
                                                fontSize: 10,
                                                color: "#9ca3af",
                                              }}
                                            >
                                              {room.room_type}
                                            </span>
                                          </div>
                                        </button>
                                      ))}
                                      {roomsHasMore && (
                                        <div
                                          style={{
                                            padding: 12,
                                            textAlign: "center",
                                            fontSize: 11,
                                            color: "#2563eb",
                                          }}
                                        >
                                          {roomsLoading
                                            ? "Loading more..."
                                            : "Scroll for more"}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <div
                                    style={{ maxHeight: 200, overflowY: "auto" }}
                                  >
                                    {!activities?.length ? (
                                      <p
                                        style={{
                                          fontSize: 11,
                                          color: "#9ca3af",
                                          textAlign: "center",
                                          padding: "8px 0",
                                          margin: 0,
                                        }}
                                      >
                                        No activities. Select scope in Summary first.
                                      </p>
                                    ) : (
                                      activities.map((m) => (
                                        <button
                                          key={m.id}
                                          type="button"
                                          onClick={() => addMilestoneTag(m)}
                                          style={{
                                            width: "100%",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8,
                                            padding: "8px 8px",
                                            fontSize: 13,
                                            textAlign: "left",
                                            background: "#fff",
                                            border: "none",
                                            borderRadius: 8,
                                            cursor: "pointer",
                                          }}
                                          onMouseEnter={(e) =>
                                            (e.currentTarget.style.background =
                                              "#f9fafb")
                                          }
                                          onMouseLeave={(e) =>
                                            (e.currentTarget.style.background =
                                              "#fff")
                                          }
                                        >
                                          {m.milestone_image?.path ? (
                                            <img
                                              src={m.milestone_image.path}
                                              alt=""
                                              style={{
                                                width: 28,
                                                height: 28,
                                                borderRadius: 4,
                                                objectFit: "cover",
                                              }}
                                            />
                                          ) : (
                                            <div
                                              style={{
                                                width: 28,
                                                height: 28,
                                                borderRadius: 4,
                                                background: "#e5e7eb",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                              }}
                                            >
                                              <FaTag
                                                size={11}
                                                color="#9ca3af"
                                              />
                                            </div>
                                          )}
                                          <span
                                            style={{
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              whiteSpace: "nowrap",
                                            }}
                                          >
                                            {m.activity_name ||
                                              m.milestone_name ||
                                              m.name}
                                          </span>
                                        </button>
                                      ))
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Tags summary footer */}
                    <div
                      style={{
                        borderTop: "1px solid #e5e7eb",
                        padding: "12px 16px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 12,
                        background: "#f9fafb",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 8,
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        {currentTags.map((tag) => (
                          <span key={tag.id} style={tagPillStyle(tag.type)}>
                            {tag.type === "name" ? (
                              <FaHome size={9} />
                            ) : (
                              <FaTag size={9} />
                            )}
                            {tag.label}
                            {!disabled && (
                              <button
                                type="button"
                                onClick={() => removeTag(tag.id)}
                                style={{
                                  marginLeft: 2,
                                  padding: 2,
                                  borderRadius: 9999,
                                  background: "transparent",
                                  border: "none",
                                  color: "#fff",
                                  cursor: "pointer",
                                  display: "flex"
                                }}
                              >
                                <FaTimes size={8} />
                              </button>
                            )}
                          </span>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={handleSaveAnnotated}
                        disabled={!canSaveAnnotated || savingImage}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "10px 18px",
                          borderRadius: 8,
                          border: "none",
                          background: "#4D6BF6",
                          color: "#fff",
                          fontSize: 13,
                          fontWeight: 500,
                          cursor:
                            !canSaveAnnotated || savingImage
                              ? "not-allowed"
                              : "pointer",
                          opacity: !canSaveAnnotated || savingImage ? 0.6 : 1,
                          fontFamily: "Inter Tight",
                          flexShrink: 0,
                        }}
                      >
                        {savingImage ? "Saving…" : "Save Image"}
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div
                style={{
                  flex: 1,
                  border: "2px dashed #d1d5db",
                  borderRadius: 12,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#6b7280",
                  fontSize: 13,
                  minHeight: 400,
                }}
              >
                Upload a file to start tagging
              </div>
            )}
          </div>
        </div>
      )}

      {/* ---------------- GALLERY TAB ---------------- */}
      {activeSubTab === "gallery" && (
        <div>
          {Object.keys(roomGroups).length === 0 &&
          savedPdfs.length === 0 &&
          !hasOtherMedia ? (
            <div
              style={{
                border: "1px dashed #d1d5db",
                borderRadius: 12,
                padding: "48px 16px",
                textAlign: "center",
                color: "#6b7280",
                fontSize: 13,
              }}
            >
              <p style={{ fontWeight: 500, margin: 0 }}>
                No saved images or PDFs yet
              </p>
              <p style={{ marginTop: 4 }}>
                Annotate and save images to see them grouped by room, or save
                PDFs to view them here
              </p>
            </div>
          ) : (
            <>
              {/* Room / Documents / Other pill filters */}
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                  marginBottom: 16,
                }}
              >
                <button
                  type="button"
                  onClick={() => setSelectedGalleryRoom(null)}
                  style={pillBase(selectedGalleryRoom === null)}
                >
                  All Photos
                </button>
                {Object.entries(roomGroups).map(([roomName, imgs]) => (
                  <button
                    key={roomName}
                    type="button"
                    onClick={() =>
                      setSelectedGalleryRoom(
                        selectedGalleryRoom === roomName ? null : roomName
                      )
                    }
                    style={pillBase(selectedGalleryRoom === roomName)}
                  >
                    {roomName} ({imgs.length})
                  </button>
                ))}
                {savedPdfs.length > 0 && (
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedGalleryRoom(
                        selectedGalleryRoom === GALLERY_TAB_PDF
                          ? null
                          : GALLERY_TAB_PDF
                      )
                    }
                    style={pillBase(selectedGalleryRoom === GALLERY_TAB_PDF)}
                  >
                    Documents ({savedPdfs.length})
                  </button>
                )}
                {hasOtherMedia && (
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedGalleryRoom(
                        selectedGalleryRoom === GALLERY_TAB_OTHER
                          ? null
                          : GALLERY_TAB_OTHER
                      )
                    }
                    style={pillBase(selectedGalleryRoom === GALLERY_TAB_OTHER)}
                  >
                    Other (
                    {normalizedOtherImages.length + normalizedOtherPdfs.length})
                  </button>
                )}
              </div>

              {/* Other tab */}
              {selectedGalleryRoom === GALLERY_TAB_OTHER ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {normalizedOtherImages.length > 0 && (
                    <GallerySection
                      title="Images"
                      countLabel={`${normalizedOtherImages.length} image${
                        normalizedOtherImages.length !== 1 ? "s" : ""
                      }`}
                      isMobile={isMobile}
                    >
                      <GalleryImageGrid
                        items={normalizedOtherImages}
                        isMobile={isMobile}
                        onViewImage={setViewingImage}
                      />
                    </GallerySection>
                  )}
                  {normalizedOtherPdfs.length > 0 && (
                    <GallerySection
                      title={
                        <>
                          <AiOutlineFilePdf size={14} color="#dc2626" />
                          Documents
                        </>
                      }
                      countLabel={`${normalizedOtherPdfs.length} file${
                        normalizedOtherPdfs.length !== 1 ? "s" : ""
                      }`}
                      isMobile={isMobile}
                    >
                      <GalleryPdfGrid
                        items={normalizedOtherPdfs}
                        isMobile={isMobile}
                        onDelete={null}
                      />
                    </GallerySection>
                  )}
                </div>
              ) : selectedGalleryRoom === GALLERY_TAB_PDF ? (
                <GallerySection
                  title={
                    <>
                      <AiOutlineFilePdf size={14} color="#dc2626" />
                      Documents
                    </>
                  }
                  countLabel={`${savedPdfs.length} file${
                    savedPdfs.length !== 1 ? "s" : ""
                  }`}
                  isMobile={isMobile}
                >
                  <GalleryPdfGrid
                    items={savedPdfs}
                    isMobile={isMobile}
                    onDelete={!disabled ? handleDeleteSaved : null}
                  />
                </GallerySection>
              ) : (
                <>
                  {Object.entries(roomGroups)
                    .filter(
                      ([roomName]) =>
                        !selectedGalleryRoom ||
                        selectedGalleryRoom === roomName
                    )
                    .map(([roomName, imgs]) => (
                      <GallerySection
                        key={roomName}
                        title={
                          <>
                            <FaHome color="#2563eb" size={14} />
                            <span style={{ textTransform: "capitalize" }}>
                              {roomName}
                            </span>
                          </>
                        }
                        countLabel={`${imgs.length} image${
                          imgs.length !== 1 ? "s" : ""
                        }`}
                        isMobile={isMobile}
                      >
                        <GalleryImageGrid
                          items={imgs}
                          isMobile={isMobile}
                          onViewImage={setViewingImage}
                          showMilestones
                        />
                      </GallerySection>
                    ))}
                  {selectedGalleryRoom === null && savedPdfs.length > 0 && (
                    <GallerySection
                      title={
                        <>
                          <AiOutlineFilePdf size={14} color="#dc2626" />
                          Documents
                        </>
                      }
                      countLabel={`${savedPdfs.length} file${
                        savedPdfs.length !== 1 ? "s" : ""
                      }`}
                      isMobile={isMobile}
                    >
                      <GalleryPdfGrid
                        items={savedPdfs}
                        isMobile={isMobile}
                        onDelete={!disabled ? handleDeleteSaved : null}
                      />
                    </GallerySection>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* Gallery full-image viewer modal */}
      {viewingImage &&
        (() => {
          const galleryTags = metaDataToTags(viewingImage.meta_data);
          return (
            <div
              onClick={() => setViewingImage(null)}
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.8)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10000,
                padding: 16,
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: "relative",
                  maxWidth: 960,
                  width: "100%",
                  maxHeight: "90vh",
                  background: "#fff",
                  borderRadius: 12,
                  overflow: "hidden",
                }}
              >
                <div style={{ position: "relative" }}>
                  <img
                    src={viewingImage.url || viewingImage.path}
                    alt=""
                    style={{
                      width: "100%",
                      maxHeight: "80vh",
                      objectFit: "contain",
                      background: "#111827",
                    }}
                  />
                  {/* Tags at stored positions */}
                  {galleryTags.map((tag) => (
                    <div
                      key={tag.id || tag.label}
                      style={{
                        position: "absolute",
                        left: `${tag.x}%`,
                        top: `${tag.y}%`,
                        transform: "translate(-50%, -100%)",
                        marginTop: -4,
                        pointerEvents: "none",
                      }}
                    >
                      <span style={tagPillStyle(tag.type)}>
                        {tag.type === "name" ? (
                          <FaHome size={9} />
                        ) : (
                          <FaTag size={9} />
                        )}
                        {tag.label}
                      </span>
                    </div>
                  ))}
                  {/* Date overlay */}
                  <div
                    style={{
                      position: "absolute",
                      top: 12,
                      right: 12,
                      background: "rgba(0,0,0,0.7)",
                      color: "#fff",
                      padding: "6px 10px",
                      borderRadius: 6,
                      fontSize: 13,
                      fontWeight: 500,
                      pointerEvents: "none",
                    }}
                  >
                    {fmtDate(formatImageDate(viewingImage))}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setViewingImage(null)}
                  style={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    padding: 8,
                    background: "#fff",
                    border: "none",
                    borderRadius: 9999,
                    cursor: "pointer",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                    display: "flex"
                  }}
                >
                  <FaTimes size={14} />
                </button>
                <div
                  style={{
                    padding: 16,
                    borderTop: "1px solid #e5e7eb",
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    {getRoomName(viewingImage.meta_data) && (
                      <span style={tagPillStyle("name")}>
                        <FaHome size={9} />
                        {getRoomName(viewingImage.meta_data)}
                      </span>
                    )}
                    {(viewingImage.meta_data?.milestones || []).map((m) => (
                      <span
                        key={m.id}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          padding: "4px 10px",
                          borderRadius: 9999,
                          border: "1px solid #d1d5db",
                          color: "#374151",
                          fontSize: 11,
                          background: "#fff",
                        }}
                      >
                        <FaTag size={9} color="#16a34a" />
                        {m.milestone_name || m.name}
                      </span>
                    ))}
                  </div>
                  {!disabled && viewingImage.id && (
                    <button
                      type="button"
                      onClick={() => {
                        handleDeleteSaved(viewingImage.id);
                        setViewingImage(null);
                      }}
                      style={{
                        padding: "6px 12px",
                        borderRadius: 8,
                        fontSize: 13,
                        fontWeight: 500,
                        background: "#fee2e2",
                        color: "#b91c1c",
                        border: "none",
                        cursor: "pointer",
                      }}
                    >
                      Delete image
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })()}
    </div>
  );
}

/* ---- small presentational helpers used by gallery ---- */

function GallerySection({ title, countLabel, isMobile, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <h3
        style={{
          fontSize: 14,
          fontWeight: 500,
          display: "flex",
          alignItems: "center",
          gap: 8,
          margin: "0 0 8px 0",
        }}
      >
        {title}
        <span
          style={{
            color: "#949daa",
            fontWeight: 400,
            fontSize: 13,
            fontFamily: "Inter Tight",
          }}
        >
          ({countLabel})
        </span>
      </h3>
      {children}
    </div>
  );
}

function GalleryImageGrid({ items, isMobile, onViewImage, showMilestones }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: isMobile
          ? "repeat(auto-fill, minmax(120px, 1fr))"
          : "repeat(auto-fill, minmax(220px, 1fr))",
        gap: isMobile ? 8 : 12,
      }}
    >
      {items.map((img) => (
        <div
          key={img.uniqueId || img.id}
          onClick={() => onViewImage?.(img)}
          style={{
            borderRadius: 8,
            overflow: "hidden",
            border: "1px solid #e5e7eb",
            background: "#f9fafb",
            cursor: "pointer",
          }}
        >
          <div
            style={{
              position: "relative",
              aspectRatio: "16/9",
              background: "#f3f4f6",
            }}
          >
            <img
              src={img.url || img.path || DEFAULT_IMAGE}
              alt=""
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
            <div
              style={{
                position: "absolute",
                top: 8,
                right: 8,
                background: "rgba(0,0,0,0.7)",
                color: "#fff",
                padding: "2px 8px",
                borderRadius: 4,
                fontSize: 10,
                fontWeight: 500,
                pointerEvents: "none",
              }}
            >
              {fmtDate(formatImageDate(img))}
            </div>
            {showMilestones && (
              <div
                style={{
                  position: "absolute",
                  bottom: 4,
                  left: 4,
                  right: 4,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 4,
                }}
              >
                {(img.meta_data?.milestones || []).map((m) => (
                  <span
                    key={m.id}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 2,
                      padding: "2px 6px",
                      borderRadius: 4,
                      background: "rgba(22,163,74,0.9)",
                      color: "#fff",
                      fontSize: 10,
                    }}
                  >
                    <FaTag size={7} />
                    {m.milestone_name || m.name}
                  </span>
                ))}
              </div>
            )}
          </div>
          {showMilestones && (img.meta_data?.milestones || []).length > 0 && (
            <div
              style={{
                padding: 8,
                display: "flex",
                flexWrap: "wrap",
                gap: 4,
                borderTop: "1px solid #e5e7eb",
                background: "#fff",
              }}
            >
              {(img.meta_data?.milestones || []).map((m) => (
                <span
                  key={m.id}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 2,
                    padding: "2px 6px",
                    borderRadius: 4,
                    border: "1px solid #e5e7eb",
                    fontSize: 10,
                    color: "#4b5563",
                  }}
                >
                  <FaTag size={7} color="#16a34a" />
                  {m.milestone_name || m.name}
                </span>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function GalleryPdfGrid({ items, isMobile, onDelete }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: isMobile
          ? "repeat(auto-fill, minmax(120px, 1fr))"
          : "repeat(auto-fill, minmax(180px, 1fr))",
        gap: isMobile ? 8 : 12,
      }}
    >
      {items.map((pdf) => (
        <div
          key={pdf.uniqueId || pdf.id}
          style={{
            position: "relative",
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            background: "#f9fafb",
            overflow: "hidden",
          }}
        >
          <a
            href={pdf.url || pdf.path}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: isMobile ? "18px 10px" : "32px 16px",
              textDecoration: "none",
              color: "inherit",
            }}
          >
            <AiOutlineFilePdf size={isMobile ? 36 : 56} color="#dc2626" />
            <span style={{ fontSize: 10, color: "#6b7280", marginTop: 8 }}>
              {fmtDate(formatImageDate(pdf))}
            </span>
          </a>
          {onDelete && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(pdf.id);
              }}
              style={{
                position: "absolute",
                top: 6,
                right: 6,
                padding: 4,
                borderRadius: 9999,
                background: "#ef4444",
                color: "#fff",
                border: "none",
                cursor: "pointer",
              }}
            >
              <IoClose size={12} />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
